import { router } from "expo-router";
import { useImperativeHandle, useRef } from "react";
import type { Ref } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { Platform, View } from "react-native";
import { Display, mq } from "react-native-unistyles";

import Button from "$src/core/components/atoms/Button/Button";
import Text from "$src/core/components/atoms/Text/Text";
import TVFocusGuideView from "$src/core/components/atoms/TVFocusGuideView";
import HeroGradientMaskedImage from "$src/core/components/molecules/HeroGradientMaskedImage";
import PlayContentButton from "$src/core/components/molecules/PlayContentButton";
import TruncatedText from "$src/core/components/molecules/TruncatedText/TruncatedText";
import { IMAGE_BLUR_DIMENSIONS } from "$src/core/constants/image";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import useTranslation from "$src/core/hooks/use-translation";
import type { ContentTypeDto, ImageDto } from "$src/core/models/dto";
import pickImageUrl from "$src/core/utils/image/pick-image-url";

import $ from "./Hero.styles";

type Props = {
  ref?: Ref<HeroRef>;
  id: string;
  type: ContentTypeDto;
  title?: string;
  categories?: string[];
  description?: string;
  image?: ImageDto;
  imageDimensions?: { width?: number; height?: number }[];
  contentContainerStyle?: StyleProp<ViewStyle>;
};

export type HeroRef = {
  focus: () => void;
};

const Hero = ({
  ref,
  id,
  type,
  title,
  categories,
  description,
  image,
  imageDimensions,
  contentContainerStyle,
}: Props) => {
  const { t } = useTranslation();

  const playButtonRef = useRef<View | null>(null);
  const infoButtonRef = useRef<View | null>(null);

  const imageUrl = image
    ? pickImageUrl(image, ["landscape", "portrait"])
    : undefined;

  const placeholderImage = useResponsiveImage(imageUrl, {
    dimensions: [IMAGE_BLUR_DIMENSIONS],
  });
  const responsiveImage = useResponsiveImage(imageUrl, {
    dimensions: imageDimensions,
  });

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (Platform.isTV) {
        playButtonRef.current?.requestTVFocus();
      } else {
        playButtonRef.current?.focus();
      }
    },
  }));

  return (
    <View style={$.container}>
      {responsiveImage && (
        <HeroGradientMaskedImage
          placeholder={placeholderImage}
          image={responsiveImage}
          style={$.image}
        />
      )}

      <View style={[$.inner, contentContainerStyle]}>
        {title && (
          <Text accessibilityRole="header" preset="heading1">
            {title}
          </Text>
        )}
        {categories && (
          <TruncatedText
            maxChars={100}
            text={categories.join(" · ")}
            onShowMore={() => router.push(`/${type}/${id}`)}
          />
        )}

        <Display mq={mq.only.width("lg")}>
          <Text
            accessibilityRole={Platform.OS === "web" ? "text" : "summary"}
            preset="body"
            style={$.description}
          >
            {description}
          </Text>
        </Display>

        <TVFocusGuideView
          style={$.actions}
          // eslint-disable-next-line react-hooks/refs
          destinations={[playButtonRef.current, infoButtonRef.current]}
          autoFocus
        >
          <PlayContentButton
            ref={playButtonRef}
            name="hero_play"
            contentId={id}
            contentType={type}
            contentTitle={title ?? ""}
            preset="primary"
            style={$.playButton}
          />
          <Button
            ref={infoButtonRef}
            name="hero_more_info"
            preset="secondary"
            icon="circle-info"
            label={t("COMMON/MORE_INFO")}
            style={$.infoButton}
            onPress={() => router.push(`/${type}/${id}`)}
            accessibilityHint={t("COMMON/MORE_INFO/CTA/HINT", {
              type: t(
                `COMMON/${type.toUpperCase() as Uppercase<typeof type>}/SINGLE`,
              ),
              title,
            })}
          />
        </TVFocusGuideView>
      </View>
    </View>
  );
};

export default Hero;
